import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Typography,
  CircularProgress,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Button,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import AddIcon from '@mui/icons-material/Add';
import getToken from '../auth/getToken';
import PageHeader from './common/PageHeader';
import RetentionNotice from './common/RetentionNotice';

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function TranscriptionRow({ transcription, onOpen, onDelete }) {
  const theme = useTheme();
  const fileName = transcription.file_url?.split('/').pop() || 'Datei';

  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{
        cursor: 'pointer',
        p: 2,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition:
          'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <InsertDriveFileOutlinedIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.3 }}
          noWrap
        >
          {fileName}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.8rem', mt: 0.25 }}
        >
          {formatDate(transcription.created_at)}
        </Typography>
      </Box>
      <Tooltip title="Löschen">
        <IconButton
          aria-label="Löschen"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{ flexShrink: 0 }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </Paper>
  );
}

function EmptyState({ onCreate }) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: 3,
        textAlign: 'center',
        borderStyle: 'dashed',
        borderColor: alpha(theme.palette.text.primary, 0.2),
      }}
    >
      <InsertDriveFileOutlinedIcon
        sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
      />
      <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
        Noch keine Transkriptionen
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mt: 0.5 }}>
        Lade eine Audio- oder Videodatei hoch, um zu starten.
      </Typography>
      <Button
        onClick={onCreate}
        variant="contained"
        startIcon={<AddIcon />}
        sx={{ mt: 2.5 }}
      >
        Neue Transkription
      </Button>
    </Paper>
  );
}

const SEITENGROESSE = 10;

const TranscriptionsListView = () => {
  const [loading, setLoading] = useState(true);
  const [transcriptions, setTranscriptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const { instance } = useMsal();
  const navigate = useNavigate();
  // Verhindert, dass Scrollen und Knopf dieselbe Seite doppelt anfordern.
  const laedtRef = useRef(false);
  const endeRef = useRef(null);

  // Eine Seite der Liste. Der Cursor kommt vom Backend und setzt hinter dem
  // letzten geladenen Eintrag fort.
  const ladeSeite = useCallback(
    async (cursor) => {
      if (laedtRef.current) return;
      laedtRef.current = true;
      setLoadError('');
      if (cursor) setLoadingMore(true);
      try {
        const token = await getToken(instance, [
          `${process.env.REACT_APP_CLIENT_ID}/.default`,
        ]);
        const query = `limit=${SEITENGROESSE}${
          cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
        }`;
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/transcriptions?${query}`,
          {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!response.ok)
          throw new Error('Fehler beim Abrufen der Transkriptionen');
        const data = await response.json();
        const neue = data.transcriptions || [];
        setTranscriptions((prev) => {
          const bekannt = new Set(prev.map((t) => t.job_id));
          return [...prev, ...neue.filter((t) => !bekannt.has(t.job_id))];
        });
        setTotal(data.total ?? 0);
        setNextCursor(data.next_cursor || null);
      } catch (error) {
        console.error('Error fetching transcriptions:', error);
        setLoadError('Transkriptionen konnten nicht geladen werden. Bitte noch einmal versuchen.');
      } finally {
        laedtRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [instance],
  );

  useEffect(() => {
    ladeSeite(null);
  }, [ladeSeite]);

  // Am Listenende automatisch nachladen. Ohne IntersectionObserver bleibt
  // der Knopf "Weitere laden".
  useEffect(() => {
    const ende = endeRef.current;
    if (!nextCursor || !ende || typeof window.IntersectionObserver !== 'function') {
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) ladeSeite(nextCursor);
      },
      { rootMargin: '200px' },
    );
    observer.observe(ende);
    return () => observer.disconnect();
  }, [nextCursor, ladeSeite]);

  const handleDelete = async (jobId) => {
    const ok = window.confirm(
      'Diese Transkription inkl. Audio, Chat und KI-Index wirklich löschen?',
    );
    if (!ok) return;
    try {
      const token = await getToken(instance, [
        `${process.env.REACT_APP_CLIENT_ID}/.default`,
      ]);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/transcriptions/${jobId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) throw new Error('Fehler beim Löschen der Transkription');
      setTranscriptions((prev) => prev.filter((t) => t.job_id !== jobId));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error deleting transcription:', error);
    }
  };

  return (
    <Box sx={{ maxWidth: 840, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PageHeader
        overline="Übersicht"
        title="Transkriptionen"
        subtitle="Alle deine bisherigen Jobs und Ergebnisse — mit Audio, Chat und KI-Funktionen."
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/transcribe')}
          >
            Neue Transkription
          </Button>
        }
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : transcriptions.length === 0 && loadError ? (
        <Alert severity="error">{loadError}</Alert>
      ) : transcriptions.length === 0 && !nextCursor ? (
        <>
          <EmptyState onCreate={() => navigate('/transcribe')} />
          <RetentionNotice sx={{ mt: 3 }} />
        </>
      ) : (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
          >
            {`${transcriptions.length} von ${total}`}
          </Typography>
          <Stack spacing={1.5}>
            {transcriptions.map((t) => (
              <TranscriptionRow
                key={t.job_id}
                transcription={t}
                onOpen={() => navigate(`/transcription-result/${t.job_id}`)}
                onDelete={() => handleDelete(t.job_id)}
              />
            ))}
          </Stack>
          <Box ref={endeRef} />
          {loadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {loadError}
            </Alert>
          )}
          {nextCursor && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                onClick={() => ladeSeite(nextCursor)}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              >
                Weitere laden
              </Button>
            </Box>
          )}
          <RetentionNotice sx={{ mt: 3 }} />
        </>
      )}
    </Box>
  );
};

export default TranscriptionsListView;
